/**
 * FlowButton — v2 button, ported from the OnFlow Design System project
 * (v2/components/core/FlowButton.jsx).
 *
 * Primary is an ember fill with a lit top edge and a soft ember shadow, so it
 * reads as a physical key rather than a coloured rectangle. Presses scale to
 * 0.97 — the one place v2 adds a press state.
 */
import type { CSSProperties, ReactNode } from 'react';

export type FlowButtonVariant = 'primary' | 'solid' | 'soft' | 'quiet' | 'danger' | 'ghost';
export type FlowButtonSize = 'sm' | 'md' | 'lg';

const SIZES: Record<FlowButtonSize, { padding: string; fontSize: number; radius: string }> = {
  sm: { padding: '9px 14px', fontSize: 13, radius: 'var(--r-xs)' },
  md: { padding: '13px 20px', fontSize: 15, radius: 'var(--r-sm)' },
  lg: { padding: '16px 24px', fontSize: 16, radius: 'var(--r-sm)' },
};

const VARIANTS: Record<FlowButtonVariant, CSSProperties> = {
  primary: {
    background: 'linear-gradient(180deg, var(--ember-lit) 0%, var(--ember) 60%)',
    color: 'var(--ember-ink)',
    border: '1px solid var(--ember-deep)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,.25), 0 6px 18px rgba(238,107,51,.22)',
    fontWeight: 650,
  },
  solid: {
    background: 'var(--ink-1)',
    color: 'var(--paper-0)',
    border: '1px solid transparent',
    boxShadow: '0 6px 18px rgba(0,0,0,.4)',
    fontWeight: 650,
  },
  soft: {
    background: 'var(--paper-3)',
    color: 'var(--ink-1)',
    border: '1px solid var(--edge)',
    boxShadow: 'var(--elev-1)',
    fontWeight: 600,
  },
  quiet: {
    background: 'transparent',
    color: 'var(--ink-2)',
    border: '1px solid var(--edge)',
    boxShadow: 'none',
    fontWeight: 600,
  },
  danger: {
    background: 'var(--off-track-wash)',
    color: 'var(--off-track)',
    border: '1px solid color-mix(in srgb, var(--off-track) 35%, transparent)',
    boxShadow: 'none',
    fontWeight: 600,
  },
  ghost: {
    background: 'transparent',
    color: 'var(--ember)',
    border: '1px solid transparent',
    boxShadow: 'none',
    fontWeight: 650,
  },
};

interface FlowButtonProps {
  variant?: FlowButtonVariant;
  size?: FlowButtonSize;
  block?: boolean;
  disabled?: boolean;
  icon?: ReactNode;
  /** Renders an <a> instead of a <button>. */
  href?: string;
  onClick?: () => void;
  type?: 'button' | 'submit';
  'aria-label'?: string;
  'data-testid'?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

export function FlowButton({
  variant = 'primary',
  size = 'md',
  block = false,
  disabled = false,
  icon,
  href,
  onClick,
  type = 'button',
  style,
  children,
  ...rest
}: FlowButtonProps) {
  const s = SIZES[size];
  const base: CSSProperties = {
    display: block ? 'flex' : 'inline-flex',
    width: block ? '100%' : undefined,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    fontFamily: 'var(--font-ui)',
    fontSize: s.fontSize,
    padding: s.padding,
    borderRadius: s.radius,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.45 : 1,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    transition: 'transform var(--t-tap) var(--ease-snap), filter var(--t-state) var(--ease-out)',
    ...VARIANTS[variant],
    ...style,
  };

  // Press feedback is applied imperatively so the component stays uncontrolled
  // and does not re-render on every pointer event.
  const press = (scale: string) => (e: { currentTarget: HTMLElement }) => {
    if (disabled) return;
    e.currentTarget.style.transform = scale;
  };

  if (href) {
    return (
      <a
        href={href}
        style={base}
        onPointerDown={press('scale(0.97)')}
        onPointerUp={press('scale(1)')}
        onPointerLeave={press('scale(1)')}
        {...rest}
      >
        {icon}
        {children}
      </a>
    );
  }

  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      style={base}
      onPointerDown={press('scale(0.97)')}
      onPointerUp={press('scale(1)')}
      onPointerLeave={press('scale(1)')}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
}
